import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Clock, Settings, Star, Target } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { ProgressRing } from '@/components/ProgressRing';
import { TempoChart } from '@/components/TempoChart';
import { Card, SectionTitle } from '@/components/ui';
import { SettingsSheet } from '@/components/SettingsSheet';
import { RECOMMENDATION } from '@/config/training';
import { t } from '@/i18n';
import { useProgress } from '@/hooks/useProgress';
import { formatTransitionKey } from '@/training/summary';
import type { SessionSummary, TransitionStats } from '@/types/training';
import './ProgressPage.css';

type Range = 'week' | 'month' | 'all';

const RANGE_DAYS: Record<Range, number> = { week: 7, month: 30, all: 3650 };
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/** Progress: what is growing, what is lagging. Not an analytics dashboard. */
export function ProgressPage() {
  const { sessions, transitions, loading, loadedAt, reload } = useProgress();
  const [range, setRange] = useState<Range>('week');
  const [settingsOpen, setSettingsOpen] = useState(false);

  const scoped = useMemo(() => {
    // Anchored on when the data was read: a clock read during render is impure
    // and would slide the window on every re-render.
    const cutoff = loadedAt - RANGE_DAYS[range] * 24 * 60 * 60 * 1000;
    return sessions.filter((session) => session.startedAt >= cutoff);
  }, [sessions, range, loadedAt]);

  const focus = useMemo(() => pickFocus(transitions), [transitions]);
  const weak = useMemo(() => rankWeak(transitions), [transitions]);
  const strong = useMemo(() => rankStrong(transitions), [transitions]);
  const chart = useMemo(() => buildChart(scoped, loadedAt), [scoped, loadedAt]);
  const totals = useMemo(() => aggregate(scoped), [scoped]);

  const settingsButton = (
    <button
      type="button"
      className="btn btn--icon"
      onClick={() => setSettingsOpen(true)}
      aria-label={t('settings.open')}
    >
      <Settings size={20} aria-hidden="true" />
    </button>
  );

  const isEmpty = !loading && sessions.length === 0;

  return (
    <AppShell headerRight={settingsButton}>
      {isEmpty ? (
        <Card section className="progress__empty">
          <p className="progress__empty-title">{t('progress.empty')}</p>
          <p className="muted">{t('progress.emptyHint')}</p>
        </Card>
      ) : (
        <>
      <div className="progress__head">
        <h1 className="progress__title">{t('progress.title')}</h1>
        <div className="progress__range" role="group" aria-label={t('progress.title')}>
          {(['week', 'month', 'all'] as Range[]).map((option) => (
            <button
              key={option}
              type="button"
              className="chip"
              aria-pressed={range === option}
              onClick={() => setRange(option)}
            >
              {t(`progress.${option}`)}
            </button>
          ))}
        </div>
      </div>

      {focus && (
        <Card section className="progress__focus">
          <div className="progress__focus-text">
            <p className="stat-label">{t('progress.focus')}</p>
            <p className="progress__focus-key">{formatTransitionKey(focus.key)}</p>
            <p className="progress__focus-goal muted tnum">
              {focus.comfortableBpm ?? '—'} BPM →{' '}
              {t('progress.goal', { value: (focus.comfortableBpm ?? 50) + 10 })}
            </p>
          </div>
          <ProgressRing value={Math.round(focus.successRate * 100)} size={130} />
        </Card>
      )}

      <section className="page-section">
        <Card>
          <SectionTitle>{t('progress.tempoGrowth')}</SectionTitle>
          <TempoChart points={chart} />
        </Card>
      </section>

      <div className="progress__pair">
        <TransitionList
          title={t('progress.weakTransitions')}
          tone="warn"
          icon={<AlertTriangle size={18} aria-hidden="true" />}
          items={weak}
        />
        <TransitionList
          title={t('progress.strongTransitions')}
          tone="good"
          icon={<Star size={18} aria-hidden="true" fill="currentColor" />}
          items={strong}
        />
      </div>

      <div className="progress__pair progress__pair--stats">
        <Card>
          <div className="progress__stat">
            <span className="home-stat__icon home-stat__icon--green">
              <Target size={20} aria-hidden="true" />
            </span>
            <div>
              <p className="stat-label">{t('progress.accuracy')}</p>
              <p className="progress__stat-value progress__stat-value--green tnum">
                {totals.accuracy === null ? '—' : `${totals.accuracy}%`}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="progress__stat">
            <span className="home-stat__icon home-stat__icon--blue">
              <Clock size={20} aria-hidden="true" />
            </span>
            <div>
              <p className="stat-label">{t('progress.avgTime')}</p>
              <p className="progress__stat-value progress__stat-value--blue tnum">
                {totals.avgTransitionSec === null
                  ? '—'
                  : `${totals.avgTransitionSec.toFixed(2)} ${t('common.seconds')}`}
              </p>
            </div>
          </div>
        </Card>
      </div>
        </>
      )}

      {settingsOpen && <SettingsSheet onClose={() => setSettingsOpen(false)} onReset={reload} />}
    </AppShell>
  );
}

interface TransitionListProps {
  title: string;
  tone: 'warn' | 'good';
  icon: React.ReactNode;
  items: TransitionStats[];
}

function TransitionList({ title, tone, icon, items }: TransitionListProps) {
  return (
    <Card>
      <div className={`progress__list-head progress__list-head--${tone}`}>
        <span>{title}</span>
        {icon}
      </div>
      {items.length === 0 ? (
        <p className="muted progress__list-empty">{t('progress.noTransitions')}</p>
      ) : (
        <ul className="progress__list">
          {items.map((item) => (
            <li key={item.key}>
              <Link to="/training/chord-change" className="progress__list-item">
                <span>{formatTransitionKey(item.key)}</span>
                <ChevronRight size={18} aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function pickFocus(transitions: TransitionStats[]): TransitionStats | undefined {
  return transitions
    .filter((item) => item.attempts >= RECOMMENDATION.minAttempts)
    .sort((a, b) => a.successRate - b.successRate)[0];
}

function rankWeak(transitions: TransitionStats[]): TransitionStats[] {
  return transitions
    .filter((item) => item.attempts >= RECOMMENDATION.minAttempts)
    .filter((item) => item.successRate < RECOMMENDATION.weakSuccessRate)
    .sort((a, b) => a.successRate - b.successRate)
    .slice(0, 2);
}

function rankStrong(transitions: TransitionStats[]): TransitionStats[] {
  return transitions
    .filter((item) => item.attempts >= RECOMMENDATION.minAttempts)
    .filter((item) => item.successRate >= RECOMMENDATION.weakSuccessRate)
    .sort((a, b) => b.successRate - a.successRate || (a.avgSec ?? 9) - (b.avgSec ?? 9))
    .slice(0, 2);
}

/** Seven day buckets ending today, each holding the best tempo reached that day. */
function buildChart(sessions: SessionSummary[], at: number) {
  const today = new Date(at || Date.now());
  const points: { label: string; value: number | null }[] = [];
  for (let offset = 6; offset >= 0; offset--) {
    const day = new Date(today);
    day.setDate(today.getDate() - offset);
    day.setHours(0, 0, 0, 0);
    const next = day.getTime() + 24 * 60 * 60 * 1000;
    const inDay = sessions.filter(
      (session) => session.startedAt >= day.getTime() && session.startedAt < next && session.bpmEnd > 0,
    );
    const best = inDay.length > 0 ? Math.max(...inDay.map((session) => session.bpmEnd)) : null;
    // getDay(): 0 = Sunday, and the labels start on Monday.
    points.push({ label: WEEKDAYS[(day.getDay() + 6) % 7], value: best });
  }
  return points;
}

function aggregate(sessions: SessionSummary[]) {
  if (sessions.length === 0) return { accuracy: null as number | null, avgTransitionSec: null as number | null };
  const withAttempts = sessions.filter((session) => session.attempts > 0);
  const accuracy =
    withAttempts.length > 0
      ? Math.round(withAttempts.reduce((sum, s) => sum + s.accuracy, 0) / withAttempts.length)
      : null;
  const times = sessions.map((s) => s.avgTransitionSec).filter((v): v is number => v !== null);
  return {
    accuracy,
    avgTransitionSec: times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : null,
  };
}
